import type { UseQueryResult } from '@tanstack/react-query';

/** React Query status flags used when merging dependent queries. */
export type DependentQueryState = Pick<
  UseQueryResult,
  'isLoading' | 'isPending' | 'isFetching' | 'isError'
>;

/**
 * Parent query in a dependent chain. `isChildEnabled` must mirror the child
 * query's `enabled` option — not the parent's `isSuccess`, which can be true
 * while the child is still disabled (e.g. the parent resolved without the id the
 * child needs).
 */
export type ParentDependentQueryState = DependentQueryState & {
  isChildEnabled: boolean;
  isSuccess: boolean;
};

/**
 * Merge React Query status flags for a dependent query chain, where the child
 * is only enabled once the parent has produced the value it depends on.
 *
 * `parent.isChildEnabled` must mirror the child's `enabled` option: a disabled
 * child reports `isPending: true` forever in RQ v5, so trusting that flag
 * unconditionally pins the merged state to pending whenever the child never
 * gets enabled — the parent erroring, or succeeding without the id the child
 * needs.
 */
export const mergeDependentQueryStates = (
  parent: ParentDependentQueryState,
  child: DependentQueryState,
) => ({
  isLoading: parent.isLoading || (parent.isSuccess && child.isLoading),
  isPending: parent.isPending || (parent.isChildEnabled && child.isPending),
  isFetching: parent.isFetching || child.isFetching,
  isError: parent.isError || child.isError,
});
